import {
  CloseButtonProps,
  IconProps,
  toast,
  ToastContainer,
  ToastContainerProps,
  TypeOptions,
} from 'react-toastify';
import { Check, IconProps as PhosphorIconProps } from '@phosphor-icons/react';
import { CheckCircle, Info, Warning, X } from '@phosphor-icons/react';
import 'react-toastify/dist/ReactToastify.css';
import { Button } from 'src/v2/commonComponents/Button';
import classNames from 'classnames';
import { SVG_SIZE_L, SVG_SIZE_M } from 'src/constants';
import styles from './Toast.module.scss';

interface ToastButton {
  label: string;
  onClick: () => void;
}

interface ToastWithButtonsProps {
  message: string;
  firstButton?: ToastButton;
  secondButton?: ToastButton;
}

interface ToastWithCustomIconProps {
  message: string;
  icon?: React.ElementType<PhosphorIconProps>;
}

export const ToastCloseButton = ({ closeToast }: CloseButtonProps) => {
  return (
    <Button
      color="transparent"
      onClick={closeToast}
      className={styles.closeButton}
    >
      <X size={SVG_SIZE_M} />
    </Button>
  );
};

export const Toast = (props: Partial<ToastContainerProps>) => {
  const getToastClassName = (type?: TypeOptions) => {
    switch (type) {
      case 'info':
        return styles.info;
      case 'success':
        return styles.success;
      case 'error':
        return styles.error;
      case 'warning':
        return styles.warning;
      default:
        return styles.info;
    }
  };

  const getToastIcon = (props: IconProps): JSX.Element | null => {
    switch (props.type) {
      case 'error':
        return <Warning size={SVG_SIZE_L} weight="fill" />;
      case 'success':
        return <CheckCircle size={SVG_SIZE_L} weight="fill" />;
      case 'warning':
        return <Warning size={SVG_SIZE_L} weight="fill" />;
      case 'info':
        return <Info size={SVG_SIZE_L} />;
      default:
        return null;
    }
  };

  return (
    <ToastContainer
      limit={5}
      style={{
        zIndex: 10000010,
      }}
      position={toast.POSITION.TOP_CENTER}
      hideProgressBar
      autoClose={3000}
      className={styles.root}
      toastClassName={(context) =>
        classNames(styles.toast, getToastClassName(context?.type))
      }
      icon={getToastIcon}
      closeButton={false}
      {...props}
    />
  );
};

export const ToastWithButtons = ({
  message,
  firstButton,
  secondButton,
}: ToastWithButtonsProps) => {
  return (
    <div className={styles.toastWithButtons}>
      <div className={styles.message}>{message}</div>

      {(firstButton || secondButton) && (
        <div className={styles.footer}>
          {firstButton && (
            <Button color="transparent" onClick={firstButton.onClick}>
              {firstButton.label}
            </Button>
          )}
          {secondButton && (
            <Button color="transparent" onClick={secondButton.onClick}>
              {secondButton.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export const ToastWithCustomIcon = ({
  message,
  icon: Icon = Check,
}: ToastWithCustomIconProps) => {
  return (
    <div className={styles.toastWithCustomIcon}>
      <Icon size={SVG_SIZE_M} />
      <div className={styles.message}>{message}</div>
    </div>
  );
};
